import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip, X, FileText, FileSpreadsheet, Download, Eye, Loader2, Camera } from "lucide-react";
import { http, formatApiError } from "../lib/api";

const ACCEPT = ".jpg,.jpeg,.png,.webp,.heic,.heif,.pdf,.doc,.docx,.xls,.xlsx";
const size = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} Mo` : `${Math.max(1, Math.round(n / 1024))} Ko`);
const isImg = (m) => m.startsWith("image/");
const blobOf = (base, id) => http.get(`${base}/files/${id}`, { responseType: "blob" }).then((r) => URL.createObjectURL(r.data));

const compress = (f) => new Promise((res) => {
  if (!/^image\/(jpeg|png|webp|heic|heif)$/.test(f.type) || f.size < 400 * 1024) return res(f);
  const u = URL.createObjectURL(f);
  const img = new Image();
  img.onerror = () => { URL.revokeObjectURL(u); res(f); };
  img.onload = () => {
    const k = Math.min(1, 1600 / Math.max(img.width, img.height));
    const c = Object.assign(document.createElement("canvas"), { width: Math.round(img.width * k), height: Math.round(img.height * k) });
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height); ctx.drawImage(img, 0, 0, c.width, c.height);
    c.toBlob((b) => { URL.revokeObjectURL(u); res(b && b.size < f.size ? new File([b], `${f.name.replace(/\.\w+$/, "") || "photo"}.jpg`, { type: "image/jpeg" }) : f); }, "image/jpeg", 0.8);
  };
  img.src = u;
});

export const AttachPicker = ({ base, files, setFiles, camera = false }) => {
  const ref = useRef(null);
  const cam = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (list) => {
    const room = 3 - files.length;
    if (list.length > room) toast.error("3 fichiers maximum par message");
    setBusy(true);
    const added = [];
    for (const raw of Array.from(list).slice(0, room)) {
      const f = await compress(raw);
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} : 10 Mo maximum`); continue; }
      const fd = new FormData(); fd.append("file", f);
      try { added.push((await http.post(`${base}/attachments`, fd)).data); } catch (e) { toast.error(`${f.name} : ${formatApiError(e)}`); }
    }
    setFiles([...files, ...added]); setBusy(false);
    ref.current.value = ""; if (cam.current) cam.current.value = "";
  };
  return (
    <>
      {camera && <>
        <input ref={cam} data-testid="msg-camera-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => pick(e.target.files)} />
        <button type="button" data-testid="msg-camera-btn" title="Prendre une photo" disabled={busy || files.length >= 3} onClick={() => cam.current.click()}
          className="px-2.5 rounded-md border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50"><Camera className="h-4 w-4" /></button>
      </>}
      <input ref={ref} data-testid="msg-attach-input" type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => pick(e.target.files)} />
      <button type="button" data-testid="msg-attach-btn" title={camera ? "Choisir une photo ou un document du téléphone" : "Joindre une photo ou un document"} disabled={busy || files.length >= 3} onClick={() => ref.current.click()}
        className="px-2.5 rounded-md border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
      </button>
    </>
  );
};

export const PendingFiles = ({ files, setFiles }) => files.length > 0 && (
  <div className="flex flex-wrap gap-2 px-3 pt-2" data-testid="msg-pending-files">
    {files.map((f) => (
      <span key={f.id} className="inline-flex items-center gap-1.5 text-xs bg-slate-100 border border-slate-200 rounded-full pl-2.5 pr-1 py-1">
        <Paperclip className="h-3 w-3" />{f.name} <span className="text-slate-400">{size(f.size)}</span>
        <button type="button" data-testid={`msg-pending-remove-${f.id}`} onClick={() => setFiles(files.filter((x) => x.id !== f.id))} className="p-0.5 rounded-full hover:bg-slate-200"><X className="h-3 w-3" /></button>
      </span>
    ))}
  </div>
);

const Modal = ({ onClose, children, title }) => (
  <div className="fixed inset-0 z-[60] bg-slate-900/80 grid place-items-center p-4" data-testid="msg-file-modal">
    <div className="bg-white text-slate-900 rounded-md w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-200"><span className="font-semibold text-sm truncate">{title}</span>
        <button data-testid="msg-file-modal-close" onClick={onClose} className="ml-auto p-1 rounded hover:bg-slate-100"><X className="h-4 w-4" /></button></div>
      <div className="flex-1 overflow-auto bg-slate-50">{children}</div>
    </div>
  </div>
);

const Thumb = ({ base, f, onOpen }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => { let u; blobOf(base, f.id).then((x) => { u = x; setUrl(x); }).catch(() => {}); return () => u && URL.revokeObjectURL(u); }, [base, f.id]);
  return (
    <button type="button" data-testid={`msg-thumb-${f.id}`} onClick={() => url && onOpen(url)} className="block h-28 w-28 rounded-md overflow-hidden bg-slate-200 border border-slate-300">
      {url ? <img src={url} alt={f.name} className="h-full w-full object-cover" /> : <Loader2 className="h-4 w-4 animate-spin m-auto mt-12 text-slate-400" />}
    </button>
  );
};

const DocCard = ({ base, f, onPreview }) => {
  const ext = f.name.split(".").pop().toLowerCase();
  const Icon = ext.startsWith("xls") ? FileSpreadsheet : FileText;
  const download = async () => {
    try { const u = await blobOf(base, f.id); Object.assign(document.createElement("a"), { href: u, download: f.name }).click(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div data-testid={`msg-doc-${f.id}`} className="flex items-center gap-2.5 bg-white border border-slate-200 rounded-md px-2.5 py-2 text-slate-800 min-w-[220px]">
      <Icon className="h-6 w-6 text-sky-700 shrink-0" />
      <div className="min-w-0"><div className="text-xs font-semibold truncate max-w-[160px]">{f.name}</div><div className="text-[10px] text-slate-500">{ext.toUpperCase()} · {size(f.size)}</div></div>
      <button type="button" data-testid={`msg-doc-preview-${f.id}`} title="Aperçu" onClick={() => onPreview(f)} className="ml-auto p-1.5 rounded hover:bg-slate-100"><Eye className="h-4 w-4" /></button>
      <button type="button" data-testid={`msg-doc-download-${f.id}`} title="Télécharger" onClick={download} className="p-1.5 rounded hover:bg-slate-100"><Download className="h-4 w-4" /></button>
    </div>
  );
};

export const AttachmentList = ({ base, items }) => {
  const [view, setView] = useState(null);
  if (!items?.length) return null;
  const preview = async (f) => {
    try {
      if (f.mime === "application/pdf") setView({ title: f.name, pdf: await blobOf(base, f.id) });
      else setView({ title: f.name, html: (await http.get(`${base}/files/${f.id}`, { params: { preview: true } })).data.html });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="flex flex-wrap gap-2 mt-1.5">
      {items.map((f) => (isImg(f.mime) ? <Thumb key={f.id} base={base} f={f} onOpen={(u) => setView({ title: f.name, img: u })} /> : <DocCard key={f.id} base={base} f={f} onPreview={preview} />))}
      {view && (
        <Modal title={view.title} onClose={() => setView(null)}>
          {view.img && <img src={view.img} alt={view.title} className="max-h-[80vh] mx-auto" />}
          {view.pdf && <iframe title={view.title} src={view.pdf} className="w-full h-[78vh] bg-white" />}
          {view.html && <div className="p-5 text-sm text-slate-800 bg-white [&_table]:border-collapse [&_td]:border [&_td]:border-slate-300 [&_td]:px-2 [&_td]:py-1 [&_p]:mb-2" dangerouslySetInnerHTML={{ __html: view.html }} />}
        </Modal>
      )}
    </div>
  );
};
